import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Campaign } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface CampaignState {
  // State
  selectedCampaign: Campaign | null;
  availableCampaigns: Campaign[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Synchronous Actions
  setSelectedCampaign: (campaign: Campaign | null) => void;
  setAvailableCampaigns: (campaigns: Campaign[]) => void;
  clearCampaign: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Async Actions
  fetchCampaigns: (filters?: { status?: string }) => Promise<void>;
  selectCampaignById: (campaignId: string) => Promise<void>;
  refreshSelectedCampaign: () => Promise<void>;
}

// ============================================================================
// STORE
// ============================================================================

export const useCampaignStore = create<CampaignState>()(
  devtools(
    persist(
      (set, get) => ({
        // ========================================================================
        // INITIAL STATE
        // ========================================================================
        selectedCampaign: null,
        availableCampaigns: [],
        isLoading: false,
        error: null,
        lastFetched: null,

        // ========================================================================
        // SYNCHRONOUS ACTIONS
        // ========================================================================

        setSelectedCampaign: (campaign) => {
          set({ 
            selectedCampaign: campaign, 
            error: null 
          });

          // Also update sessionStorage for compatibility
          if (campaign) {
            sessionStorage.setItem('selectedCampaignId', campaign.id);
          } else {
            sessionStorage.removeItem('selectedCampaignId');
          }
        },

        setAvailableCampaigns: (campaigns) => {
          set({ 
            availableCampaigns: campaigns,
            lastFetched: Date.now()
          });
        },

        clearCampaign: () => {
          set({ 
            selectedCampaign: null,
            error: null 
          });
          sessionStorage.removeItem('selectedCampaignId');
        },

        setLoading: (isLoading) => {
          set({ isLoading });
        },

        setError: (error) => {
          console.error('❌ Campaign Store Error:', error);
          set({ error, isLoading: false });
        },

        // ========================================================================
        // ASYNC ACTIONS
        // ========================================================================

        fetchCampaigns: async (filters = {}) => {
          set({ isLoading: true, error: null });

          try {
            const { CampaignService } = await import('../services');
            
            // Fetch campaigns with filters (default to ACTIVE)
            const response = await CampaignService.getCampaigns({
              status: (filters.status || 'ACTIVE') as 'ACTIVE' | 'INACTIVE' | 'DISABLED' | 'HIDDEN'
            });

            if (response.data) {
              set({
                availableCampaigns: response.data,
                isLoading: false,
                error: null,
                lastFetched: Date.now()
              });


              // Load campaign configurations into cache
              const { loadCampaignsFromDatabase } = await import(
                '../../enrollment/core/config/campaigns.config'
              );
              await loadCampaignsFromDatabase(response.data);
              

              // Restore selected campaign from sessionStorage if it exists
              const { selectedCampaign } = get();
              if (!selectedCampaign) {
                const savedCampaignId = sessionStorage.getItem('selectedCampaignId');
                if (savedCampaignId) {
                  const campaign = response.data.find(c => c.id === savedCampaignId);
                  if (campaign) {
                    set({ selectedCampaign: campaign });
                  }
                }
              }
            } else {
              throw new Error('Failed to load campaigns');
            }
          } catch (error: any) {
            console.error('❌ Campaign Store: Failed to fetch campaigns', error);
            set({
              error: error.message || 'Failed to fetch campaigns',
              isLoading: false,
              availableCampaigns: []
            });
          }
        },

        selectCampaignById: async (campaignId) => {
          set({ isLoading: true, error: null });

          try {
            const { CampaignService } = await import('../services');
            
            // Fetch full campaign details
            const response = await CampaignService.getCampaignById(campaignId);
            const campaign = response.data || response; // Handle both APIResponse and direct Campaign

            set({
              selectedCampaign: campaign as Campaign,
              isLoading: false,
              error: null
            });

            // Update sessionStorage
            sessionStorage.setItem('selectedCampaignId', campaignId);


            // If this campaign isn't in available campaigns, add it
            const { availableCampaigns } = get();
            if (!availableCampaigns.find(c => c.id === campaignId)) {
              set({
                availableCampaigns: [...availableCampaigns, campaign as Campaign]
              });
            }
          } catch (error: any) {
            console.error('❌ Campaign Store: Failed to select campaign', error);
            set({
              error: error.message || 'Failed to select campaign',
              isLoading: false
            });
            throw error;
          }
        },

        refreshSelectedCampaign: async () => {
          const { selectedCampaign } = get();
          if (!selectedCampaign) {
            console.warn('⚠️ Campaign Store: No selected campaign to refresh');
            return;
          }


          try {
            const { CampaignService } = await import('../services');
            
            const response = await CampaignService.getCampaignById(selectedCampaign.id);
            const campaign = response.data || response; // Handle both APIResponse and direct Campaign

            set({ selectedCampaign: campaign as Campaign });


            // Update in available campaigns list
            const { availableCampaigns } = get();
            set({
              availableCampaigns: availableCampaigns.map(c =>
                c.id === (campaign as Campaign).id ? (campaign as Campaign) : c
              )
            });
          } catch (error: any) {
            console.error('❌ Campaign Store: Failed to refresh campaign', error);
            // Don't throw - this is a background refresh
          }
        },
      }),
      {
        name: 'campaign-storage',
        partialize: (state) => ({
          selectedCampaign: state.selectedCampaign,
          lastFetched: state.lastFetched
        }),
      }
    ),
    { name: 'CampaignStore' }
  )
);

